import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
} from '@mui/material';
import React, { useEffect } from 'react';
import { FormattedMessage } from 'react-intl';

interface ConfirmationDialogProps {
    open: boolean;
    title: string | React.ReactNode;
    message: string | React.ReactNode;
    onConfirm: () => void;
    onCancel: () => void;
    confirmText?: string | React.ReactNode;
    cancelText?: string | React.ReactNode;
    mode?:
        | 'error'
        | 'primary'
        | 'inherit'
        | 'secondary'
        | 'success'
        | 'info'
        | 'warning';
    /** Optional keyboard event handler to use when dialog is open*/
    onKeydown?: (event: KeyboardEvent) => void;
    /**
     * Optional third action rendered between Cancel and Confirm — for
     * choices that are neither (e.g. "Save and close" next to a
     * discard-and-close confirm).
     */
    extraAction?: {
        label: string | React.ReactNode;
        onClick: () => void;
        disabled?: boolean;
        testId?: string;
    };
    /** Disables every action (e.g. while the extra action is running). */
    busy?: boolean;
    /** Disables the confirm action only. */
    confirmDisabled?: boolean;
    /**
     * Whether a click outside the dialog cancels it. Off for a question a
     * stray second click must not answer.
     */
    cancelOnOutsideClick?: boolean;
}

const ConfirmationDialog: React.FC<ConfirmationDialogProps> = ({
    open,
    title,
    message,
    onConfirm,
    onCancel,
    confirmText,
    cancelText,
    mode = 'error',
    onKeydown,
    extraAction,
    busy = false,
    confirmDisabled = false,
    cancelOnOutsideClick = true,
}) => {
    // Add or remove keydown event listener based on dialog open state
    useEffect(() => {
        if (open && onKeydown) {
            document.addEventListener('keydown', onKeydown);
        }

        if (!open && onKeydown) {
            document.removeEventListener('keydown', onKeydown);
        }

        return () => {
            if (onKeydown) {
                document.removeEventListener('keydown', onKeydown);
            }
        };
    }, [open, onKeydown]);

    return (
        <Dialog
            open={open}
            onClose={(_, reason) => {
                if (reason !== 'backdropClick' || cancelOnOutsideClick) {
                    onCancel();
                }
            }}
            aria-labelledby="confirmation-dialog-title"
            aria-describedby="confirmation-dialog-description"
            PaperProps={{ elevation: 0 }}
        >
            <DialogTitle id="confirmation-dialog-title">{title}</DialogTitle>
            <DialogContent>
                <DialogContentText id="confirmation-dialog-description">
                    {message}
                </DialogContentText>
            </DialogContent>
            <DialogActions>
                <Button
                    onClick={onCancel}
                    color="primary"
                    autoFocus
                    disabled={busy}
                    data-testid="confirmation-dialog-cancel"
                >
                    {cancelText || <FormattedMessage id="cancel" />}
                </Button>
                {extraAction && (
                    <Button
                        onClick={extraAction.onClick}
                        color="primary"
                        variant="contained"
                        disabled={busy || extraAction.disabled}
                        data-testid={
                            extraAction.testId ??
                            'confirmation-dialog-extra-action'
                        }
                    >
                        {extraAction.label}
                    </Button>
                )}
                <Button
                    onClick={onConfirm}
                    color={mode}
                    disabled={busy || confirmDisabled}
                    data-testid="confirmation-dialog-confirm"
                >
                    {confirmText || <FormattedMessage id="confirm" />}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default ConfirmationDialog;
